import { useState, useEffect, useMemo } from 'react';
import { Search, TrendingUp, Home, Calendar, Calculator, CreditCard, Wallet, Building2, ChartBar, FileText, BookOpen, Percent, DollarSign, PiggyBank, Receipt, ShieldCheck, AlertCircle, Scale, Briefcase, ClipboardCheck, FileSignature, Clock, Gift, Key, HandCoins, Landmark, type LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Fuse from 'fuse.js';

// Icon map for converting string names to components
const iconMap: Record<string, LucideIcon> = {
  'TrendingUp': TrendingUp,
  'Home': Home,
  'Calendar': Calendar,
  'Calculator': Calculator,
  'CreditCard': CreditCard,
  'Wallet': Wallet,
  'Building2': Building2,
  'ChartBar': ChartBar,
  'FileText': FileText,
  'BookOpen': BookOpen,
  'Percent': Percent,
  'DollarSign': DollarSign,
  'PiggyBank': PiggyBank,
  'Receipt': Receipt,
  'ShieldCheck': ShieldCheck,
  'AlertCircle': AlertCircle,
  'Scale': Scale,
  'Briefcase': Briefcase,
  'ClipboardCheck': ClipboardCheck,
  'FileSignature': FileSignature,
  'Clock': Clock,
  'Gift': Gift,
  'Key': Key,
  'HandCoins': HandCoins,
  'Landmark': Landmark,
};

interface Guide {
  title: string;
  description: string;
  slug: string;
  icon: string;
  gradient: string;
  section?: string;
}

interface KlarsprakSearchProps {
  allGuides: Array<{
    section: string;
    description: string;
    guides: Omit<Guide, 'section'>[];
  }>;
  onGuideClick: (slug: string) => void;
}

const KlarsprakSearch = ({ allGuides, onGuideClick }: KlarsprakSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Flatten all guides for search
  const flatGuides = useMemo(() => {
    const guides: Guide[] = [];
    allGuides.forEach((section) => {
      section.guides.forEach((guide) => {
        guides.push({
          ...guide,
          section: section.section
        });
      });
    });
    return guides;
  }, [allGuides]);

  // Configure Fuse.js
  const fuse = useMemo(() => {
    return new Fuse(flatGuides, {
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'description', weight: 0.3 },
        { name: 'section', weight: 0.2 }
      ],
      threshold: 0.3,
      includeScore: true,
      minMatchCharLength: 2,
      ignoreLocation: true,
    });
  }, [flatGuides]);

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const fuseResults = fuse.search(searchQuery);
    return fuseResults.slice(0, 6).map(r => r.item);
  }, [searchQuery, fuse]);

  return (
    <div className="mx-auto mt-6 max-w-xl space-y-4">
      <div className="relative">
        <div className="relative">
          <Input
            type="text"
            placeholder="Vad vill du lära dig om idag?"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 rounded-lg bg-background pl-4 pr-11 text-base shadow-none"
          />
          <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        </div>
        {searchQuery && (
          <p className="ml-1 mt-2 text-sm text-muted-foreground">
            Hittade {filteredResults.length} guide{filteredResults.length !== 1 ? 'r' : ''}
          </p>
        )}
      </div>

      {searchQuery && filteredResults.length > 0 && (
        <Card className="max-h-[480px] overflow-y-auto rounded-lg p-3">
          <div className="space-y-3">
            {filteredResults.map((guide, index) => {
              return (
                <Card 
                  key={index}
                  className="cursor-pointer transition-colors hover:border-primary/50 hover:bg-primary/5"
                  onClick={() => onGuideClick(guide.slug)}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {(() => {
                          const IconComponent = iconMap[guide.icon];
                          return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
                        })()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="mb-1 text-base leading-6">{guide.title}</CardTitle>
                        <CardDescription className="text-sm leading-5">
                          {guide.description}
                        </CardDescription>
                        <p className="text-xs text-muted-foreground mt-2">
                          {guide.section}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </Card>
      )}

      {searchQuery && filteredResults.length === 0 && (
        <Card className="rounded-lg p-5 text-center">
          <p className="text-muted-foreground">
            Inga resultat hittades för "{searchQuery}"
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Försök med andra sökord eller bläddra bland guiderna nedan
          </p>
        </Card>
      )}
    </div>
  );
};

export default KlarsprakSearch;
